import { useLayoutEffect, useCallback, useContext } from 'react';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { Box, Tooltip } from '@mui/material';
import Paper from '@mui/material/Paper';
import { dateFormatChange } from '../../../utils/date';
import { statuses } from '../../../utils/constants';
import { useNavigate } from 'react-router-dom';
import { StoreContext } from '../../../components/Store';
import { isEORIUser } from '../../../utils/roles';

const DashboardTable = ({
  shipments,
  shipmentsTotalCount,
  paginationSettings,
  handlePaginationSettingsChange,
  loading,
}) => {
  const store = useContext(StoreContext);
  const [dashboardState, setDashboardState] = store.dashboardState;

  const apiRef = useGridApiRef();

  // function to save snapshot of DataGrid state
  const saveSnapshot = useCallback(() => {
    if (apiRef?.current?.exportState) {
      const currentState = apiRef.current.exportState();
      setDashboardState(currentState);
    }
  }, [apiRef]);

  // before the page is rendered restore the DataGrid state (filters, sorting .etc)
  useLayoutEffect(() => {
    apiRef.current?.restoreState(dashboardState);
    window.addEventListener('beforeunload', saveSnapshot);

    return () => {
      window.removeEventListener('beforeunload', saveSnapshot);
      saveSnapshot();
    };
  }, [saveSnapshot]);

  const navigate = useNavigate();
  const columns = [
    {
      field: 'declarationType',
      headerName: 'Type',
      flex: 1,
      headerClassName: 'table-header',
      editable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const declarationType = params.row.declarationType || '';
        const transportInfo = params.row.transportTypeDescription;
        const transportTypeId = params.row.modeOfTransportId || '';

        // Create meaningful tooltip based on declaration type
        let tooltipText = '';
        if (transportTypeId === 1) {
          tooltipText = 'RoRo Unaccompanied';
        } else if (transportTypeId === 2) {
          tooltipText = 'RoRo Accompanied';
        } else if (transportTypeId === 3) {
          tooltipText = 'Euroshuttle Services';
        } else if (transportTypeId === 4) {
          tooltipText = 'Road';
        } else if (transportInfo && transportInfo.trim() !== '') {
          tooltipText = transportInfo;
        }

        const hasTooltip = tooltipText !== '';

        const cellContent = (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              color: declarationType ? '#000' : '#999',
              cursor: hasTooltip ? 'help' : 'default',
            }}
          >
            {declarationType || '-'}
          </Box>
        );

        return hasTooltip ? (
          <Tooltip title={tooltipText} arrow>
            {cellContent}
          </Tooltip>
        ) : (
          cellContent
        );
      },
    },
    {
      field: 'jobReference',
      headerName: 'Your Reference',
      flex: 1,
      headerClassName: 'table-header',
      editable: false,
    },
    {
      field: 'lrn',
      headerName: 'LRN',
      flex: 1,
      headerClassName: 'table-header',
      editable: false,
    },
    {
      field: 'vehicleIdentity',
      headerName: 'Vehicle Identity',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
    },
    {
      field: 'departureDate',
      headerName: 'Departure Date',
      type: 'number',
      flex: 1,
      editable: false,
      align: 'left',
      headerAlign: 'Left',
      headerClassName: 'table-header',
      renderCell: (params) => (params.row.departureDate ? dateFormatChange(params.row.departureDate) : ''),
    },
    {
      field: 'arrivalDateTime',
      headerName: 'Arrival Date',
      flex: 1,
      editable: false,
      align: 'left',
      headerAlign: 'Left',
      headerClassName: 'table-header',
      renderCell: (params) => (params.row.arrivalDateTime ? dateFormatChange(params.row.arrivalDateTime) : ''),
    },
    ...(isEORIUser()
      ? [
          {
            field: 'company',
            headerName: 'Company',
            flex: 1,
            editable: false,
            headerClassName: 'table-header',
          },
        ]
      : []),
    {
      field: 'totalConsignments',
      headerName: '# of Consignments',
      flex: 1,
      editable: false,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'table-header',
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      editable: false,
      headerClassName: 'table-header',
      headerAlign: 'center',
      textAlign: 'center',
      renderCell: (params) => {
        return (
          <Box
            sx={{
              backgroundColor: {
                [statuses.ACCEPTED]: '#AEDD94',
                [statuses.ERRORS]: '#FFB4B0',
                [statuses.REJECTED]: '#fff',
                [null]: '#fff',
              }[params.value.toUpperCase()],
              width: '100%',
              height: '100%',
              textAlign: 'center',
              fontWeight: 'bold',
            }}
          >
            {params.value}
          </Box>
        );
      },
    },
    {
      field: 'click',
      headerName: '',
      width: 50,
      editable: false,
      headerClassName: 'table-header',
      renderCell: (params) => (
        <ExitToAppIcon
          onClick={() =>
            navigate('/ShipmentView', { state: { res: params.row.headerId, companyId: params.row.companyId } })
          }
          className="svg-icons"
          style={{ cursor: 'pointer', color: '#1464F6' }}
        />
      ),
    },
  ];

  //   if (loading) return (
  //   <Box sx={{ width: '100%' }}>
  //     {Array.from({length: 25}, (x, i) => <Skeleton sx={{py: 1, my: 2}} key={i} />)}
  //   </Box>
  // );
  return (
    <Paper sx={{ width: '100%' }}>
      <DataGridPro
        pagination
        paginationMode="server"
        paginationModel={paginationSettings}
        onPaginationModelChange={handlePaginationSettingsChange}
        rowCount={shipmentsTotalCount}
        rows={shipments}
        columns={columns}
        getRowId={(row) => {
          if (!row?.headerId) {
            console.error('Missing headerId in row:', row);
          }
          return row?.headerId ?? Math.random(); //TODO: Investigate this on the backend
        }}
        pageSizeOptions={[20, 50, 100]}
        sx={{ border: 0 }}
        getRowClassName={(params) => {
          return params.row.status.toUpperCase() === statuses.ERRORS
            ? 'dashboard-table-highlight'
            : 'dashboard-table-non-highlight';
        }}
        loading={loading}
        apiRef={apiRef}
        initialState={dashboardState}
        disableColumnFilter
      />
    </Paper>
  );
};

export default DashboardTable;
